// Prints a self-contained badge artifact HTML string via a hidden iframe:
// `iframe.contentWindow.print()` reuses the same vector-crisp browser print
// path as the on-screen preview (no rasterization), and the artifact's own
// `@page { size: 3.375in 2.125in }` rule drives the label-printer page size.
//
// Sandbox tokens are the minimum that lets the host page drive printing:
//   allow-same-origin — `sandbox=""` puts the frame in an opaque origin, so
//     the host's `contentWindow.print()` is a cross-origin access and throws
//     "Permission denied to access property print on cross-origin object".
//   allow-modals — print() is a modal; the sandboxed-modals flag blocks it.
// allow-scripts is deliberately NOT granted, and that is what keeps
// allow-same-origin safe here: no script executes in the document at all, so
// there is nothing inside able to use same-origin access to reach the parent.
// Granting allow-scripts alongside allow-same-origin would let the document
// remove its own sandbox — never add it. The on-screen preview iframe keeps a
// fully restrictive `sandbox=""`; it never needs to print.
/**
 * @registry Prints a self-contained badge artifact HTML string via a hidden sandboxed iframe.
 */
export function printBadgeArtifact(html: string): void {
  const iframe = document.createElement('iframe');
  iframe.setAttribute('sandbox', 'allow-same-origin allow-modals');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = 'none';
  iframe.style.visibility = 'hidden';

  const cleanup = () => {
    if (iframe.parentNode) {
      iframe.parentNode.removeChild(iframe);
    }
  };

  iframe.onload = () => {
    try {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
    } finally {
      // Most browsers block on window.print() until the dialog closes, but
      // some (notably Chromium async print preview) return immediately —
      // give the print dialog a generous window before tearing the iframe
      // down rather than removing it out from under an open dialog.
      setTimeout(cleanup, 60000);
    }
  };

  iframe.srcdoc = html;
  document.body.appendChild(iframe);
}
